import { useState } from 'react';
import { api } from '../../api';
import type { OnboardingProgress, OnboardingStatus, SchemaDeliveryMode } from '../../types';
import { VerificationStep } from './steps/VerificationStep';
import { BusinessProfileStep } from './steps/BusinessProfileStep';
import { MarketContextStep } from './steps/MarketContextStep';
import { GoalsCompetitorsStep } from './steps/GoalsCompetitorsStep';
import { LaunchPrepStep } from './steps/LaunchPrepStep';
import { PlaceholderStep } from './steps/PlaceholderStep';
import { AfiTimingExpectations } from './AfiTimingExpectations';

// ── Step definitions ──────────────────────────────────────────────────────────

interface StepDef {
  id: 1 | 2 | 3 | 4 | 5;
  label: string;
  description: string;
}

const STEPS: StepDef[] = [
  { id: 1, label: 'Domain Verification',    description: 'Install your tracking snippet' },
  { id: 2, label: 'Market Context',         description: 'Reach, locations & audience' },
  { id: 3, label: 'Business Profile',       description: 'Confirm your audience & offerings' },
  { id: 4, label: 'Goals & Competitors',    description: 'Set your primary goal' },
  { id: 5, label: 'Schedule & Preferences', description: 'Configure your audit cadence' },
];

// ── Progress calculation ──────────────────────────────────────────────────────

function calcProgressPct(status: OnboardingStatus): number {
  const checks = [
    status.verified,
    status.businessProfileComplete,
    status.progress.reach_profile ?? false,
    status.progress.primary_location ?? false,
    status.progress.audience_confirmed ?? false,
    status.progress.goal_chosen ?? false,
    status.progress.competitor_chosen ?? false,
    status.progress.schedule_confirmed ?? false,
  ];
  const done = checks.filter(Boolean).length;
  return Math.round((done / checks.length) * 100);
}

// ── Step state helpers ────────────────────────────────────────────────────────

type StepState = 'locked' | 'unlocked' | 'active' | 'completed';

function getStepState(
  stepId: 1 | 2 | 3 | 4 | 5,
  activeStep: 1 | 2 | 3 | 4 | 5,
  status: OnboardingStatus,
  visitedSteps: Set<number>,
): StepState {
  const isActive = stepId === activeStep;
  const isCompleted = isStepCompleted(stepId, status);

  if (isCompleted) return isActive ? 'active' : 'completed';

  // Step 1 is always unlocked
  if (stepId === 1) return isActive ? 'active' : 'unlocked';

  // Steps 2+ are unlocked once verified OR if the user has previously visited them
  const unlocked = status.verified || visitedSteps.has(stepId);
  if (!unlocked) return 'locked';
  return isActive ? 'active' : 'unlocked';
}

function isStepCompleted(stepId: 1 | 2 | 3 | 4 | 5, status: OnboardingStatus): boolean {
  switch (stepId) {
    case 1: return status.verified;
    case 2: return !!(status.progress.reach_profile && status.progress.primary_location && status.progress.audience_confirmed);
    case 3: return status.businessProfileComplete;
    case 4: return status.progress.goal_chosen === true && status.progress.competitor_chosen === true;
    case 5: return status.progress.schedule_confirmed === true;
  }
}

// ── Component ─────────────────────────────────────────────────────────────────

interface Props {
  domainId: string;
  domain: string;
  tier: string;
  initialStatus: OnboardingStatus;
  schemaDeliveryMode?: SchemaDeliveryMode;
  onBack: () => void;
}

export function OnboardingWizard({ domainId, domain, tier, initialStatus, schemaDeliveryMode, onBack }: Props) {
  const [status, setStatus] = useState<OnboardingStatus>(initialStatus);
  const [activeStep, setActiveStep] = useState<1 | 2 | 3 | 4 | 5>(
    initialStatus.progress.currentStep ?? 1,
  );
  const [visitedSteps] = useState<Set<number>>(new Set([activeStep]));
  const [jumpWarning, setJumpWarning] = useState('');
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  // AFI-G2: show timing expectations interstitial once after step 5 completes
  const [showTimingStep, setShowTimingStep] = useState(false);

  const progressPct = calcProgressPct(status);

  function handleStepClick(stepId: 1 | 2 | 3 | 4 | 5) {
    if (stepId === activeStep) return;

    const state = getStepState(stepId, activeStep, status, visitedSteps);
    if (state === 'locked') {
      setJumpWarning('Verify your domain first to unlock the next steps.');
      return;
    }

    setJumpWarning('');
    visitedSteps.add(stepId);
    setActiveStep(stepId);
    setIsMobileNavOpen(false);

    // Persist currentStep in the background
    api.onboarding
      .patchProgress(domainId, { currentStep: stepId } as Partial<OnboardingProgress>)
      .catch(() => null);
  }

  function handleStatusUpdate(next: OnboardingStatus) {
    setStatus(next);
  }

  async function handleNext() {
    if (activeStep < 5) {
      const next = (activeStep + 1) as 1 | 2 | 3 | 4 | 5;
      visitedSteps.add(next);
      setActiveStep(next);
      await api.onboarding
        .patchProgress(domainId, { currentStep: next } as Partial<OnboardingProgress>)
        .catch(() => null);
    }
  }

  async function handleBack() {
    if (activeStep > 1) {
      const prev = (activeStep - 1) as 1 | 2 | 3 | 4 | 5;
      setActiveStep(prev);
      await api.onboarding
        .patchProgress(domainId, { currentStep: prev } as Partial<OnboardingProgress>)
        .catch(() => null);
    }
  }

  return (
    <div className="min-h-screen bg-brand-paper">
      {/* AFI-G2: timing expectations interstitial (shown once after completing step 5) */}
      {showTimingStep ? (
        <main className="flex min-h-screen items-center justify-center px-6 py-12">
          <AfiTimingExpectations onContinue={onBack} />
        </main>
      ) : (
      <>
      {/* ── Progress bar ── */}
      <div className="h-1 bg-gray-100 sticky top-0 z-10">
        <div
          className="h-full bg-brand-green transition-all duration-300"
          style={{ width: `${progressPct}%` }}
          role="progressbar"
          aria-valuenow={progressPct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`Onboarding ${progressPct}% complete`}
        />
      </div>

      {/* ── Header ── */}
      <header className="border-b border-gray-100 bg-white px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          {/* Mobile hamburger */}
          <button
            className="sm:hidden text-brand-muted hover:text-brand-ink"
            onClick={() => setIsMobileNavOpen((v) => !v)}
            aria-label="Toggle step navigation"
          >
            ☰
          </button>
          <div>
            <p className="text-xs text-brand-muted uppercase tracking-wide">Setup wizard</p>
            <p className="font-semibold text-brand-ink">{domain}</p>
          </div>
        </div>
        <button
          onClick={onBack}
          className="text-sm text-brand-muted hover:text-brand-ink inline-flex items-center gap-1"
        >
          ← Back to dashboard
        </button>
      </header>

      <div className="flex flex-1">
        {/* ── Step list (left panel) ── */}
        <nav
          className={`
            w-64 shrink-0 border-r border-gray-100 bg-white py-6
            sm:block
            ${isMobileNavOpen ? 'block fixed inset-0 z-20 w-72 overflow-y-auto' : 'hidden sm:block'}
          `}
          aria-label="Onboarding steps"
        >
          {isMobileNavOpen && (
            <div className="flex justify-end px-4 mb-4">
              <button
                onClick={() => setIsMobileNavOpen(false)}
                className="text-brand-muted hover:text-brand-ink text-sm"
              >
                Close ✕
              </button>
            </div>
          )}

          <ul className="space-y-1 px-3">
            {STEPS.map((step) => {
              const state = getStepState(step.id, activeStep, status, visitedSteps);
              const completed = isStepCompleted(step.id, status);
              return (
                <li key={step.id}>
                  <button
                    onClick={() => handleStepClick(step.id)}
                    className={`
                      w-full flex items-start gap-3 px-3 py-3 rounded-xl text-left transition-colors text-sm
                      ${state === 'active'
                        ? 'bg-brand-green/10 text-brand-green font-medium'
                        : state === 'completed'
                        ? 'text-brand-green hover:bg-gray-50 cursor-pointer'
                        : state === 'unlocked'
                        ? 'text-brand-ink hover:bg-gray-50 cursor-pointer'
                        : 'text-gray-300 cursor-not-allowed pointer-events-none'
                      }
                    `}
                    aria-current={state === 'active' ? 'step' : undefined}
                    aria-disabled={state === 'locked'}
                  >
                    <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-xs font-medium
                      ${completed ? 'border-brand-green bg-brand-green text-white' : 'border-current'}`}>
                      {completed ? '✓' : step.id}
                    </span>
                    <span className="flex-1">
                      <span className="block">{step.label}</span>
                      <span className="block text-xs font-normal opacity-70 mt-0.5">{step.description}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Mobile nav backdrop */}
        {isMobileNavOpen && (
          <div
            className="fixed inset-0 z-10 bg-black/30 sm:hidden"
            onClick={() => setIsMobileNavOpen(false)}
          />
        )}

        {/* ── Content area ── */}
        <main className="flex-1 px-6 py-8 max-w-2xl">
          {jumpWarning && (
            <div className="mb-4 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
              {jumpWarning}
            </div>
          )}

          {activeStep === 1 && (
            <VerificationStep
              domainId={domainId}
              domain={domain}
              status={status}
              onStatusUpdate={handleStatusUpdate}
              onContinue={handleNext}
            />
          )}
          {activeStep === 2 && (
            <MarketContextStep
              domainId={domainId}
              domain={domain}
              tier={tier}
              status={status}
              onStatusUpdate={handleStatusUpdate}
              onContinue={handleNext}
              onSkip={handleBack}
            />
          )}
          {activeStep === 3 && (
            <BusinessProfileStep
              domainId={domainId}
              domain={domain}
              status={status}
              onStatusUpdate={handleStatusUpdate}
              onContinue={handleNext}
              onSkip={handleBack}
            />
          )}
          {activeStep === 4 && (
            <GoalsCompetitorsStep
              domainId={domainId}
              domain={domain}
              tier={tier}
              status={status}
              onStatusUpdate={handleStatusUpdate}
              onContinue={handleNext}
              onSkip={handleBack}
            />
          )}
          {activeStep === 5 && (
            <LaunchPrepStep
              domainId={domainId}
              domain={domain}
              tier={tier}
              status={status}
              onStatusUpdate={handleStatusUpdate}
              onContinue={handleBack}
              onSkip={handleBack}
              onComplete={() => setShowTimingStep(true)}
            />
          )}
          {activeStep !== 1 && activeStep !== 2 && activeStep !== 3 && activeStep !== 4 && activeStep !== 5 && (
            <PlaceholderStep
              step={activeStep}
              stepLabel={STEPS[activeStep - 1]!.label}
              onBack={handleBack}
            />
          )}
        </main>
      </div>
      </> /* end !showTimingStep */
      )}
    </div>
  );
}
